/**
 * Login-security client helpers.
 *
 * Fetches public flags from ``/api/super_admin/login-security/client-config/``
 * and, only when the matching layer is enabled:
 *   * renders a honeypot field (caller)
 *   * attaches the HMAC-signed timing token
 *   * loads FingerprintJS to mint ``user_device_id`` (localStorage)
 *
 * ``device_id`` is ALWAYS attached to the login POST. The server ignores it
 * when the device layer is off. If client-config failed, the server may still
 * have the layer on (compiled defaults are all-on) — matching mobile.
 *
 * FingerprintJS is best-effort. A UUID is minted immediately so a blocked
 * CDN, private mode, or a submit before the script loads cannot produce
 * HTTP 429 "This device could not be verified".
 */

import { API } from '../config';
import api from './api';
import { DEVICE_STORAGE_KEY, randomDeviceId, shouldFallbackToJwtLogin } from './loginSecurityIds';

const FP_CDN = 'https://openfpcdn.io/fingerprintjs/v4/iife.min.js';
const FP_TIMEOUT_MS = 2000;

let cachedConfig = null;
let configPromise = null;

export function fetchLoginSecurityConfig() {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (configPromise) return configPromise;
  configPromise = api
    .get(API.ENDPOINTS.LOGIN_SECURITY_CLIENT_CONFIG)
    .then((r) => {
      cachedConfig = r.data || {};
      return cachedConfig;
    })
    .catch(() => {
      // Do not cache a failed fetch — the next sign-in should retry.
      configPromise = null;
      return {
        honeypot_enabled: false,
        timing_check_enabled: false,
        device_limit_enabled: false,
      };
    });
  return configPromise;
}

function readStoredDeviceId() {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(DEVICE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function persistDeviceId(id) {
  if (!id || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DEVICE_STORAGE_KEY, id);
  } catch {
    /* quota / private mode */
  }
}

function loadFingerprintScript() {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.FingerprintJS) return Promise.resolve(window.FingerprintJS);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), FP_TIMEOUT_MS);
    const script = document.createElement('script');
    script.src = FP_CDN;
    script.async = true;
    script.onload = () => {
      clearTimeout(timer);
      resolve(window.FingerprintJS || null);
    };
    script.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    try {
      document.head.appendChild(script);
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

async function fingerprintVisitorId() {
  const FingerprintJS = await loadFingerprintScript();
  if (!FingerprintJS) return '';
  try {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return (result && result.visitorId) || '';
  } catch {
    return '';
  }
}

/**
 * Persistent device id for login-security layer 5.
 *
 * ``deviceLimitEnabled`` only controls whether FingerprintJS is loaded
 * (zero footprint when the layer is off). An id is always returned.
 */
export async function getDeviceId(deviceLimitEnabled = true) {
  const existing = readStoredDeviceId();
  if (existing) return existing;

  if (deviceLimitEnabled) {
    const visitorId = await fingerprintVisitorId();
    if (visitorId) {
      persistDeviceId(visitorId);
      return visitorId;
    }
  }

  const id = randomDeviceId();
  persistDeviceId(id);
  return id;
}

/**
 * Extra fields to merge into the login POST body.
 * Honeypot is left empty on purpose (humans never fill it).
 */
export async function buildLoginExtras() {
  const cfg = await fetchLoginSecurityConfig();
  const extras = {};
  if (cfg.honeypot_enabled) {
    const el = typeof document !== 'undefined' ? document.getElementById('website_url') : null;
    extras.website_url = el ? el.value : '';
  }
  if (cfg.timing_check_enabled && cfg.timing_token) {
    extras.form_timing = cfg.timing_token;
  }
  extras.device_id = await getDeviceId(Boolean(cfg.device_limit_enabled));
  return extras;
}

export { DEVICE_STORAGE_KEY, randomDeviceId, shouldFallbackToJwtLogin };
